import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import StatsCard from "../../components/Card/StatsCard";
import LoadingOverlay from "../../components/Loading/LoadingOverlay";
import PayoutDetailsTable from "../../components/Table/PayoutDetailsTable";
import TransactionsTable from "../../components/Table/TransactionsTable";
import PayoutModal from "../../components/Dialog/PayoutModal";
import { open_api_with_cookies } from "../../Utils/network";
import * as XLSX from "xlsx";
import styles from "./PayoutDetails.module.css";
import calendar from "../../assets/Icons/calendarIcon.svg";
import rupee from "../../assets/Icons/ruppee.png";
import CustomAlert from "../../components/Alerts/CustomAlert";
import { addAlert, removeAlert } from "../../Utils/helper";

const PayoutDetails = () => {
  // Get expert_id from URL params
  const { id } = useParams();
  // Get query params from URL
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const month = queryParams.get("month") || "";
  const year = queryParams.get("year") || "";
  const page = queryParams.get("page") || 1;

  const [payoutData, setPayoutData] = useState({
    payouts: [],
    transactions: [],
  });
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(parseInt(page));
  const [pagination, setPagination] = useState({
    count: 0,
    next_page: null,
    previous_page: null,
    max_pages: 0,
    total_count: 0,
  });
  const [expertName, setExpertName] = useState("");
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [alerts, setAlerts] = useState([]); // Holds multiple alerts
  const [showAlert, setShowAlert] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    fetchPayoutDetails();
  }, [id, month, year, currentPage]);

  const handleAddAlert = (title, message, type, autoClose = false) => {
    addAlert(title, message, type, autoClose, setAlerts);
    setShowAlert(true); // Show alert when adding a new alert
  };

  const handleRemoveAlert = (id) => {
    removeAlert(id, setAlerts);
    if (alerts.length <= 1) {
      setShowAlert(false); // Hide alert if there are no more alerts to show
    }
  };

  const fetchPayoutDetails = async () => {
    setLoading(true);
    try {
      let prevMonth = month - 1;
      let prevYear = year;

      if (prevMonth === 0) {
        prevMonth = 12;
        prevYear = year - 1;
      }
      const response = await open_api_with_cookies.get(
        `/dashboard/payout/${id}/?cur_month=${month}&cur_year=${year}&page=${currentPage}&prev_month=${prevMonth}&prev_year=${prevYear}`
      );

      setPayoutData(response.data.data);
      setPagination({
        count: response.data.count,
        next_page: response.data.next_page,
        previous_page: response.data.previous_page,
        max_pages: response.data.max_pages,
        total_count: response.data.total_count,
      });

      if (response.data.data.payouts && response.data.data.payouts.length > 0) {
        setExpertName(response.data.data.payouts[0].expert_name);
      }
    } catch (error) {
      console.error("Error fetching payout details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    // Update URL with new page number
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("page", newPage);
    window.history.pushState(
      {},
      "",
      `${location.pathname}?${searchParams.toString()}`
    );
    setCurrentPage(newPage);
  };

  const handlePayButtonClick = () => {
    setShowPayoutModal(true);
  };

  const handlePayoutSuccess = () => {
    // Refresh data after a successful payout update
    fetchPayoutDetails();
    handleAddAlert(
      "Payout Updated",
      "Payout updated successfully",
      "success",
      true
    );
  };

  // Function to clean sheet names for Excel
  const cleanSheetName = (name) => {
    // Remove invalid characters and limit length to 31 characters
    return name
      .replace(/[\\/?*[\]:]/g, "") // Remove invalid characters
      .substring(0, 31) // Limit length
      .trim();
  };

  // Function to fetch all pages of data
  const fetchAllPagesData = async () => {
    const allTransactions = [];
    let currentPageNum = 1;
    let hasMorePages = true;

    while (hasMorePages) {
      try {
        let prevMonth = month - 1;
        let prevYear = year;

        if (prevMonth === 0) {
          prevMonth = 12;
          prevYear = year - 1;
        }

        const response = await open_api_with_cookies.get(
          `/dashboard/payout/${id}/?cur_month=${month}&cur_year=${year}&page=${currentPageNum}&prev_month=${prevMonth}&prev_year=${prevYear}`
        );

        if (response.data.data.transactions) {
          allTransactions.push(...response.data.data.transactions);
        }

        hasMorePages = response.data.next_page !== null;
        currentPageNum++;
      } catch (error) {
        console.error("Error fetching page data:", error);
        hasMorePages = false;
      }
    }

    return allTransactions;
  };

  const exportToExcel = async () => {
    if (!payoutData.transactions || payoutData.transactions.length === 0) {
      handleAddAlert("No data to export", "No data to export", "error", true);
      return;
    }

    setIsExporting(true);
    try {
      // Fetch all pages of data
      const allTransactions = await fetchAllPagesData();

      // Create a workbook
      const wb = XLSX.utils.book_new();

      // Group transactions by workshop_title and session_title
      const workshopGroups = {};
      const sessionGroups = {};

      allTransactions.forEach((transaction) => {
        // Process workshop transactions
        if (transaction.workshop_title) {
          const cleanTitle = cleanSheetName(transaction.workshop_title);
          if (!workshopGroups[cleanTitle]) {
            workshopGroups[cleanTitle] = [];
          }
          workshopGroups[cleanTitle].push({
            "User Name": transaction.user_name,
            "Purchase Date": new Date(
              transaction.purchase_date
            ).toLocaleDateString(),
            "Purchase Price (₹)": transaction.workshop_purchase_price,
            "Expert Earning (₹)": transaction.expert_earning,
            "Expert Earning (%)": transaction.percentage_of_expert_earning,
          });
        }

        // Process session transactions
        if (transaction.session_title) {
          const cleanTitle = cleanSheetName(transaction.session_title);
          if (!sessionGroups[cleanTitle]) {
            sessionGroups[cleanTitle] = [];
          }
          sessionGroups[cleanTitle].push({
            "User Name": transaction.user_name,
            "Purchase Date": new Date(
              transaction.purchase_date
            ).toLocaleDateString(),
            "Purchase Price (₹)": transaction.session_purchase_price,
            "Expert Earning (₹)": transaction.expert_earning,
            "Expert Earning (%)": transaction.percentage_of_expert_earning,
          });
        }
      });

      // Create a sheet for each workshop
      Object.keys(workshopGroups).forEach((workshop) => {
        const ws = XLSX.utils.json_to_sheet(workshopGroups[workshop]);
        XLSX.utils.book_append_sheet(wb, ws, workshop);
      });

      // Create a sheet for each session
      Object.keys(sessionGroups).forEach((session) => {
        const ws = XLSX.utils.json_to_sheet(sessionGroups[session]);
        XLSX.utils.book_append_sheet(wb, ws, session);
      });

      // Create a sheet with all transactions
      const allTransactionsData = allTransactions.map((transaction) => ({
        "User Name": transaction.user_name,
        "Workshop Title": transaction.workshop_title || "-",
        "Session Title": transaction.session_title || "-",
        "Purchase Date": new Date(
          transaction.purchase_date
        ).toLocaleDateString(),
        "Workshop Price (₹)": transaction.workshop_purchase_price || "-",
        "Session Price (₹)": transaction.session_purchase_price || "-",
        "Expert Earning (₹)": transaction.expert_earning,
        "Expert Earning (%)": transaction.percentage_of_expert_earning,
      }));
      const allTransactionsSheet =
        XLSX.utils.json_to_sheet(allTransactionsData);
      XLSX.utils.book_append_sheet(
        wb,
        allTransactionsSheet,
        "All Transactions"
      );

      // Generate filename with expert name, month and year
      const monthNames = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      const previousMonthIndex = month
        ? (parseInt(month, 10) - 2 + 12) % 12
        : null;
      const previousMonthName =
        previousMonthIndex !== null ? monthNames[previousMonthIndex] : "All";
      const fileName = `${
        expertName || "Expert"
      }_Transactions_${previousMonthName}_${year || "All"}.xlsx`;

      // Write and download the file
      XLSX.writeFile(wb, fileName);
      handleAddAlert("Exported", "Exported successfully", "success", true);
    } catch (error) {
      console.error("Error exporting data:", error);
      handleAddAlert("Export Failed", "Failed to export data", "error", true);
    } finally {
      setIsExporting(false);
    }
  };

  // Create stats card data
  const createStatsCardData = () => {
    if (!payoutData.payouts || payoutData.payouts.length === 0) return [];

    const payout = payoutData.payouts[0];
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const previousMonthIndex = month
      ? (parseInt(month, 10) - 2 + 12) % 12
      : null;
    const previousMonthName =
      previousMonthIndex !== null ? monthNames[previousMonthIndex] : "All";

    return [
      {
        icon: rupee,
        title: "Total Payout (in ₹)",
        amount: "₹ ",
        value: payout.amount?.toLocaleString() || "0",
      },
      {
        icon: calendar,
        title: "Paid for the period",
        value: `${previousMonthName} ${year}`,
      },
    ];
  };

  if (loading) {
    return <LoadingOverlay />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Payout Details for {expertName || "Expert"}</h1>
        <div>
          <button
            className={styles.exportButton}
            onClick={exportToExcel}
            disabled={isExporting}
          >
            {isExporting ? "Exporting..." : "Export as excel"}
          </button>
          {payoutData.payouts[0].payout_status === 1 && (
            <button className={styles.payButton} onClick={handlePayButtonClick}>
              Pay
            </button>
          )}
        </div>
      </div>

      <div className={styles.statsCardContainer}>
        <StatsCard data={createStatsCardData()} />
      </div>

      <h2 className={styles.sectionTitle}>Payout details</h2>
      <PayoutDetailsTable payouts={payoutData.payouts} />

      <h2 className={styles.sectionTitle}>All transaction</h2>
      <TransactionsTable
        transactions={payoutData.transactions}
        pagination={pagination}
        currentPage={currentPage}
        onPageChange={handlePageChange}
      />

      {showPayoutModal &&
        payoutData.payouts &&
        payoutData.payouts.length > 0 && (
          <PayoutModal
            payout={payoutData.payouts[0]}
            onClose={() => setShowPayoutModal(false)}
            onSuccess={handlePayoutSuccess}
          />
        )}
      {showAlert &&
        alerts.map((alert) => (
          <CustomAlert
            key={alert.id}
            title={alert.title}
            message={alert.message}
            onClose={() => handleRemoveAlert(alert.id)}
            autoClose={alert.autoClose}
            type={alert.type}
          />
        ))}
    </div>
  );
};

export default PayoutDetails;
